import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtMXN, fmtDate, fmtDateTime, downloadBlob, todayStr } from "../lib/api";
import Topbar from "../components/Topbar";
import StatusBadge from "../components/StatusBadge";
import EmptyState from "../components/EmptyState";
import { useAuth } from "../lib/auth";
import { Eye, CheckCircle2, XCircle, AlertTriangle, Download, Trash2, RotateCcw, FileUp, Landmark, UserPlus } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import InvoiceUploadModal from "../components/InvoiceUploadModal";
import EventualInvoiceModal from "../components/EventualInvoiceModal";

const HIGH_VALUE_THRESHOLD = 10000;

export default function Invoices() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  // Fix WH79 (2026-10-15): Pagos enlaza a Facturas con el filtro ya puesto
  // (?filtro=received o ?filtro=flagged).
  const [filter, setFilter] = useState(() => {
    const f = new URLSearchParams(window.location.search).get("filtro");
    return ["received", "validated", "paid", "rejected", "flagged", "nopo"].includes(f) ? f : "all";
  });
  // Fix WH79 (2026-10-15): Finanzas también valida y justifica discrepancias de
  // precio. Rechazar sigue siendo solo de corporativo.
  const puedeValidar = ["corporate_admin", "treasury"].includes(user?.role);
  const puedeRechazar = user?.role === "corporate_admin";
  const [review, setReview] = useState(null); // invoice under price review
  const [reviewReason, setReviewReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [toDelete, setToDelete] = useState(null); // invoice queued for deletion
  const [deleteReason, setDeleteReason] = useState("");
  const [folioConfirm, setFolioConfirm] = useState("");
  const [uploadNoPo, setUploadNoPo] = useState(false);
  const [uploadEventual, setUploadEventual] = useState(false); // Fix WH80 (2026-10-15)
  const [toRestore, setToRestore] = useState(null); // invoice queued for restoration
  const [restoreReason, setRestoreReason] = useState("");

  const exportToExcel = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filter !== "all" && filter !== "flagged" && filter !== "deleted") params.set("status", filter);
      const res = await api.get(`/invoices/export?${params.toString()}`, { responseType: "blob" });
      downloadBlob(res.data, `facturas_${todayStr()}.xlsx`);
      toast.success(t("export.success"));
    } catch {
      toast.error(t("export.error"));
    } finally {
      setExporting(false);
    }
  };

  const load = () => {
    const params = new URLSearchParams();
    if (showDeleted && user?.role === "corporate_admin") params.set("include_deleted", "true");
    return api.get(`/invoices?${params.toString()}`).then((r) => setList(r.data));
  };
  useEffect(() => { load(); }, [showDeleted]);

  // Fix WH77 (2026-10-14): facturas cuyo pago ya aparecio en el archivo del banco. Si la
  // consulta falla o el rol no tiene acceso, simplemente no se muestra la marca.
  const [confirmadasBanco, setConfirmadasBanco] = useState(() => new Set());
  const rolUsuario = user?.role;
  useEffect(() => {
    if (!rolUsuario || rolUsuario === "supplier") return;
    api.get("/ops/bank-payments/confirmed-invoice-ids")
      .then((r) => setConfirmadasBanco(new Set(r.data?.invoice_ids || [])))
      .catch(() => {});
  }, [rolUsuario]);

  const openDoc = (inv, kind) => {
    const url = kind === "pdf" ? inv.pdf_url : inv.xml_url;
    if (!url || !url.startsWith("/api/uploads/")) { toast.info(t("invoices.noFile")); return; }
    const token = localStorage.getItem("chizy_token");
    window.open(`${process.env.REACT_APP_BACKEND_URL}${url}&auth=${encodeURIComponent(token)}`, "_blank");
  };

  const validate = async (id, action) => {
    try {
      await api.post(`/invoices/${id}/decision`, { action });
      toast.success(action === "validate" ? t("invoices.validated") : t("invoices.rejected"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  };

  const submitReview = async (action) => {
    if (reviewReason.trim().length < 5) {
      toast.error(t("invoices.reviewReasonRequired"));
      return;
    }
    setBusy(true);
    try {
      await api.post(`/invoices/${review.id}/price-review`, { action, reason: reviewReason.trim() });
      toast.success(action === "justify" ? t("invoices.priceJustified") : t("invoices.priceRejected"));
      setReview(null);
      setReviewReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  const openDelete = (inv) => {
    setToDelete(inv);
    setDeleteReason("");
    setFolioConfirm("");
  };

  const isHighValue = (inv) => inv && Number(inv.total || 0) > HIGH_VALUE_THRESHOLD;
  const folioMatches = !toDelete || !isHighValue(toDelete) || folioConfirm.trim() === (toDelete.folio_fiscal || "").trim();
  const canSubmitDelete = !!toDelete && deleteReason.trim().length >= 10 && folioMatches && (!isHighValue(toDelete) || folioConfirm.trim().length > 0);

  const submitDelete = async () => {
    if (deleteReason.trim().length < 10) {
      toast.error(t("invoices.deleteReasonMin"));
      return;
    }
    if (isHighValue(toDelete) && folioConfirm.trim() !== (toDelete.folio_fiscal || "").trim()) {
      toast.error(t("invoices.deleteHighValueMismatch"));
      return;
    }
    setBusy(true);
    try {
      await api.delete(`/invoices/${toDelete.id}`, { data: { reason: deleteReason.trim() } });
      toast.success(t("invoices.deleteSuccess"));
      setToDelete(null);
      setDeleteReason("");
      setFolioConfirm("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  const openRestore = (inv) => {
    setToRestore(inv);
    setRestoreReason("");
  };

  const submitRestore = async () => {
    if (restoreReason.trim().length < 10) {
      toast.error(t("invoices.deleteReasonMin"));
      return;
    }
    setBusy(true);
    try {
      await api.post(`/invoices/${toRestore.id}/restore`, { reason: restoreReason.trim() });
      toast.success(t("invoices.restoreSuccess"));
      setToRestore(null);
      setRestoreReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  const confirmReception = async (inv) => {
    try {
      await api.post(`/invoices/${inv.id}/confirm-reception`, {});
      toast.success(t("invoices.receptionConfirmed"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  };

  const filtered = filter === "all"
    ? list
    : filter === "flagged"
      ? list.filter((i) => i.price_mismatch && i.price_review_status === "flagged")
      : filter === "nopo"
        ? list.filter((i) => i.has_po === false && !i.deleted_at)
        : filter === "deleted"
          ? list.filter((i) => !!i.deleted_at)
          : list.filter((i) => i.status === filter && !i.deleted_at);

  const flaggedCount = list.filter((i) => i.price_mismatch && i.price_review_status === "flagged").length;
  const withholdingMismatches = user?.role === "corporate_admin"
    ? list.filter((i) => i.withholding_mismatch && !i.deleted_at)
    : [];

  return (
    <>
      <Topbar title={t("invoices.title")} subtitle={t("invoices.subtitle", { count: list.length })} />
      <div className="p-6 space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-xl p-1 w-fit">
          {[
            { v: "all", l: t("invoices.filterAll") },
            { v: "received", l: t("invoices.filterReceived") },
            { v: "validated", l: t("invoices.filterValidated") },
            { v: "paid", l: t("invoices.filterPaid") },
            { v: "rejected", l: t("invoices.filterRejected") },
          ].map((f) => (
            <button key={f.v} onClick={() => setFilter(f.v)} data-testid={`invoice-filter-${f.v}`}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition ${
                filter === f.v ? "bg-[#1A1A1A] text-white" : "text-slate-600 hover:bg-slate-100"
              }`}>
              {f.l}
            </button>
          ))}
          <button onClick={() => setFilter("flagged")} data-testid="invoice-filter-flagged"
            className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition flex items-center gap-1.5 ${
              filter === "flagged" ? "bg-red-600 text-white" : "text-red-600 hover:bg-red-50"
            }`}>
            <AlertTriangle className="w-3.5 h-3.5" /> {t("invoices.filterFlagged")}
            {flaggedCount > 0 && (
              <span className={`px-1.5 rounded-full text-[10px] font-bold ${filter === "flagged" ? "bg-white text-red-600" : "bg-red-600 text-white"}`}>{flaggedCount}</span>
            )}
          </button>
          <button onClick={() => setFilter("nopo")} data-testid="invoice-filter-nopo"
            className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition ${
              filter === "nopo" ? "bg-slate-800 text-white" : "text-slate-600 hover:bg-slate-100"
            }`}>
            {t("invoices.filterNoPo")}
          </button>
          {user?.role === "corporate_admin" && showDeleted && (
            <button onClick={() => setFilter("deleted")} data-testid="invoice-filter-deleted"
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition flex items-center gap-1.5 ${
                filter === "deleted" ? "bg-slate-700 text-white" : "text-slate-600 hover:bg-slate-100"
              }`}>
              <Trash2 className="w-3.5 h-3.5" /> {t("invoices.filterDeleted")}
            </button>
          )}
        </div>
          {user?.role === "corporate_admin" && (
            <button onClick={() => { setShowDeleted((v) => !v); setFilter("all"); }} data-testid="invoices-toggle-deleted"
              className={`inline-flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-bold border transition ${
                showDeleted ? "bg-slate-100 border-slate-300 text-slate-800" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}>
              <Trash2 className="w-3.5 h-3.5" /> {showDeleted ? t("invoices.hideDeleted") : t("invoices.showDeleted")}
            </button>
          )}
          {user?.role === "corporate_admin" && (
            <button data-testid="upload-invoice-no-po-btn" onClick={() => setUploadNoPo(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#1A1A1A] hover:bg-black text-white rounded-xl text-sm font-bold">
              <FileUp className="w-4 h-4" /> {t("invoices.uploadNoPo")}
            </button>
          )}
          {/* Fix WH80 (2026-10-15): proveedores de una sola vez, sin darlos de alta. */}
          {user?.role === "corporate_admin" && (
            <button data-testid="upload-invoice-eventual-btn" onClick={() => setUploadEventual(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-300 hover:bg-slate-50 text-[#1A1A1A] rounded-xl text-sm font-bold">
              <UserPlus className="w-4 h-4" /> {t("invoices.uploadEventual")}
            </button>
          )}
          <button data-testid="invoices-export-btn" onClick={exportToExcel} disabled={exporting}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-bold disabled:opacity-50">
            <Download className="w-4 h-4" /> {exporting ? t("export.exporting") : t("export.button")}
          </button>
        </div>

        {withholdingMismatches.length > 0 && (
          <div data-testid="withholding-mismatch-banner" className="bg-amber-50 border border-amber-200 rounded-2xl p-4 space-y-2">
            <div className="flex items-center gap-2 text-amber-800 font-bold text-sm">
              <AlertTriangle className="w-4 h-4" /> {t("invoices.withholdingBannerTitle")}
            </div>
            {withholdingMismatches.map((inv) => (
              <div key={inv.id} className="flex items-center justify-between gap-3 text-xs text-amber-800 bg-white/60 rounded-lg px-3 py-2"
                data-testid={`withholding-mismatch-${inv.uuid_cfdi?.slice(-6)}`}>
                <span>
                  {inv.supplier_name} · {inv.po_folio} — {t("invoices.withholdingBannerDetail", {
                    actual: fmtMXN(inv.isr_withholding_amount || 0),
                    expected: fmtMXN(inv.expected_isr_withholding || 0),
                  })}
                </span>
                <button data-testid={`withholding-edit-supplier-${inv.uuid_cfdi?.slice(-6)}`}
                  onClick={() => navigate(`/proveedores/${inv.supplier_id}?edit=fiscal`)}
                  className="flex-shrink-0 px-2.5 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-[11px] font-bold">
                  {t("invoices.goEditSupplier")}
                </button>
              </div>
            ))}
          </div>
        )}

        {filtered.length === 0 ? (
          <EmptyState title={t("invoices.noResultsTitle")} message={t("invoices.noResultsMsg")} />
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("invoices.uuid")}</th>
                  <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("invoices.supplier")}</th>
                  <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("invoices.po")}</th>
                  <th className="text-right px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("invoices.total")}</th>
                  <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("invoices.dueDate")}</th>
                  <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("invoices.status")}</th>
                  <th className="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((inv, idx) => {
                  const flagged = inv.price_mismatch && inv.price_review_status === "flagged";
                  const isDeleted = !!inv.deleted_at;
                  return (
                  <motion.tr key={inv.id} initial={{ opacity: 0 }} animate={{ opacity: isDeleted ? 0.7 : 1 }} transition={{ delay: idx * 0.03 }}
                    className={`border-b border-slate-100 last:border-0 ${
                      isDeleted ? "bg-slate-100" : flagged ? "bg-red-50 hover:bg-red-100" : "hover:bg-slate-50"
                    }`}
                    data-testid={`invoice-row-${inv.uuid_cfdi?.slice(-6)}`}>
                    <td className="px-6 py-3.5 font-mono text-[11px] text-slate-600 max-w-xs truncate">{inv.uuid_cfdi}</td>
                    <td className="px-6 py-3.5 text-sm font-semibold">
                      {inv.supplier_name}
                      {/* Fix WH80 (2026-10-15): proveedor eventual (no registrado). */}
                      {inv.eventual_supplier && (
                        <div className="flex flex-col gap-0.5 mt-0.5" data-testid={`invoice-eventual-${inv.id}`}>
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-sky-100 text-sky-800 text-[10px] font-bold w-fit whitespace-nowrap">
                            {t("invoices.eventualBadge")}
                          </span>
                          {inv.eventual_description && (
                            <span className="text-[11px] font-normal text-slate-500">{inv.eventual_description}</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-3.5 font-mono text-sm">
                      {inv.has_po === false ? (
                        <span data-testid={`invoice-nopo-badge-${inv.uuid_cfdi?.slice(-6)}`}
                          className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-200 text-slate-700 text-[10px] font-bold">
                          {t("invoices.noPo")}
                        </span>
                      ) : inv.po_folio}
                    </td>
                    <td className="px-6 py-3.5 text-sm text-right">
                      <div className={`font-bold ${flagged ? "text-red-700" : ""}`}>{fmtMXN(inv.total)}</div>
                      {/* Fix WH68 (2026-10-09): el precio se compara sin IVA. En rojo,
                          la diferencia del subtotal (la que bloquea); en gris, la del
                          total cuando solo cambia el IVA o el descuento. */}
                      {inv.price_mismatch && (
                        <div data-testid={`invoice-price-mismatch-${inv.uuid_cfdi?.slice(-6)}`}
                          className="text-[11px] font-semibold text-red-600 flex items-center gap-1 justify-end mt-0.5">
                          <AlertTriangle className="w-3 h-3" />
                          {inv.po_subtotal != null ? (
                            <>{t("invoices.subtotalVsPo")} {fmtMXN(inv.po_subtotal)} ({inv.subtotal_difference > 0 ? "+" : ""}{fmtMXN(inv.subtotal_difference)})</>
                          ) : (
                            <>{t("invoices.vsPo")} {fmtMXN(inv.po_total)} ({inv.total_difference > 0 ? "+" : ""}{fmtMXN(inv.total_difference)})</>
                          )}
                        </div>
                      )}
                      {!inv.price_mismatch && inv.tax_mismatch && (
                        <div data-testid={`invoice-tax-diff-${inv.uuid_cfdi?.slice(-6)}`}
                          className="text-[11px] font-semibold text-slate-500 flex items-center gap-1 justify-end mt-0.5">
                          {t("invoices.vsPo")} {fmtMXN(inv.po_total)} ({inv.total_difference > 0 ? "+" : ""}{fmtMXN(inv.total_difference)})
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-3.5 text-sm text-slate-500">{fmtDate(inv.due_date)}</td>
                    <td className="px-6 py-3.5">
                      <div className="flex flex-col gap-1">
                        <StatusBadge status={inv.status} />
                        {inv.status === "paid" && confirmadasBanco.has(inv.id) && (
                          <span data-testid={`invoice-bank-confirmed-${inv.id}`} title={t("invoices.bankConfirmedTitle")}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 whitespace-nowrap text-[10px] font-bold w-fit">
                            <Landmark className="w-3 h-3" /> {t("invoices.bankConfirmed")}
                          </span>
                        )}
                        {isDeleted && (
                          <span data-testid={`invoice-deleted-badge-${inv.uuid_cfdi?.slice(-6)}`}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-700 text-white text-[10px] font-bold w-fit">
                            <Trash2 className="w-3 h-3" /> {t("invoices.deletedBadge")}
                          </span>
                        )}
                        {flagged && !isDeleted && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-600 text-white text-[10px] font-bold w-fit">
                            <AlertTriangle className="w-3 h-3" /> {t("invoices.flagBadge")}
                          </span>
                        )}
                        {inv.tax_mismatch && !inv.price_mismatch && !isDeleted && (
                          <span data-testid={`invoice-tax-badge-${inv.uuid_cfdi?.slice(-6)}`}
                            title={t("invoices.taxBadgeHint")}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-200 text-slate-700 text-[10px] font-bold w-fit">
                            {t("invoices.taxBadge")}
                          </span>
                        )}
                        {inv.price_review_status === "justified" && !isDeleted && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold w-fit">
                            {t("invoices.justifiedBadge")}
                          </span>
                        )}
                        {inv.withholding_mismatch && !isDeleted && (
                          <span data-testid={`invoice-withholding-badge-${inv.uuid_cfdi?.slice(-6)}`}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-bold w-fit">
                            <AlertTriangle className="w-3 h-3" /> {t("invoices.withholdingBadge")}
                          </span>
                        )}
                        {inv.has_po === false && inv.service_confirmation && !isDeleted && (
                          <span data-testid={`invoice-reception-confirmed-${inv.uuid_cfdi?.slice(-6)}`}
                            className="text-[10px] text-emerald-700 font-semibold">
                            {t("invoices.confirmedBy", {
                              name: inv.service_confirmation.confirmed_by_name || "",
                              date: fmtDateTime(inv.service_confirmation.confirmed_at),
                            })}
                          </span>
                        )}
                        {isDeleted && (
                          <span className="text-[10px] text-slate-500">
                            {t("invoices.deletedAt", { date: fmtDateTime(inv.deleted_at) })} {inv.deleted_by_name ? t("invoices.deletedBy", { name: inv.deleted_by_name }) : ""}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-3.5">
                      <div className="flex items-center gap-1.5 justify-end">
                        {inv.pdf_url?.startsWith("/api/uploads/") ? (
                          <>
                            <button data-testid={`invoice-pdf-btn-${inv.uuid_cfdi?.slice(-6)}`} onClick={() => openDoc(inv, "pdf")}
                              className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center" title={t("invoices.viewPdf")}>
                              <Eye className="w-3.5 h-3.5 text-slate-600" />
                            </button>
                            <button data-testid={`invoice-xml-btn-${inv.uuid_cfdi?.slice(-6)}`} onClick={() => openDoc(inv, "xml")}
                              className="px-2 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-[10px] font-bold text-slate-600" title={t("invoices.viewXml")}>
                              XML
                            </button>
                          </>
                        ) : (
                          <button onClick={() => toast.info(t("invoices.noFile"))}
                            className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center" title={t("invoices.view")}>
                            <Eye className="w-3.5 h-3.5 text-slate-600" />
                          </button>
                        )}
                        {["branch_manager", "corporate_admin"].includes(user?.role) && inv.has_po === false && !inv.service_confirmation && !isDeleted && (
                          <button data-testid={`invoice-confirm-reception-btn-${inv.uuid_cfdi?.slice(-6)}`} onClick={() => confirmReception(inv)}
                            className="px-2.5 py-1.5 bg-[#1A1A1A] hover:bg-black text-white rounded-lg text-[11px] font-bold flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" /> {t("invoices.confirmReception")}
                          </button>
                        )}
                        {puedeValidar && flagged && !isDeleted && (
                          <button data-testid={`invoice-review-price-btn-${inv.uuid_cfdi?.slice(-6)}`} onClick={() => { setReview(inv); setReviewReason(""); }}
                            className="px-2.5 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-[11px] font-bold flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" /> {t("invoices.reviewPrice")}
                          </button>
                        )}
                        {puedeValidar && inv.status === "received" && !flagged && !isDeleted && (
                          <>
                            <button data-testid={`invoice-validate-btn-${inv.uuid_cfdi?.slice(-6)}`} onClick={() => validate(inv.id, "validate")}
                              className="px-2.5 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-[11px] font-bold flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" /> {t("invoices.validate")}
                            </button>
                            {puedeRechazar && (
                              <button onClick={() => validate(inv.id, "reject")}
                                className="px-2.5 py-1.5 bg-white border border-red-200 text-red-600 hover:bg-red-50 rounded-lg text-[11px] font-bold flex items-center gap-1">
                                <XCircle className="w-3 h-3" /> {t("invoices.reject")}
                              </button>
                            )}
                          </>
                        )}
                        {user?.role === "corporate_admin" && !isDeleted && (
                          <button data-testid={`invoice-delete-btn-${inv.uuid_cfdi?.slice(-6)}`} onClick={() => openDelete(inv)}
                            className="px-2.5 py-1.5 bg-white border border-red-200 text-red-600 hover:bg-red-50 rounded-lg text-[11px] font-bold flex items-center gap-1"
                            title={t("invoices.deleteAction")}>
                            <Trash2 className="w-3 h-3" /> {t("invoices.deleteAction")}
                          </button>
                        )}
                        {user?.role === "corporate_admin" && isDeleted && (
                          <button data-testid={`invoice-restore-btn-${inv.uuid_cfdi?.slice(-6)}`} onClick={() => openRestore(inv)}
                            className="px-2.5 py-1.5 bg-white border border-slate-300 text-slate-700 hover:bg-slate-100 rounded-lg text-[11px] font-bold flex items-center gap-1">
                            <RotateCcw className="w-3 h-3" /> {t("invoices.restoreAction")}
                          </button>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {uploadNoPo && (
        <InvoiceUploadModal po={null} onClose={() => setUploadNoPo(false)} onSuccess={load} />
      )}

      {uploadEventual && (
        <EventualInvoiceModal onClose={() => setUploadEventual(false)} onSuccess={load} />
      )}

      {review && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setReview(null)}>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            data-testid="invoice-price-review-modal"
            className="bg-white rounded-2xl border border-slate-200 p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-xl bg-red-100 text-red-600 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div>
                <div className="font-display font-bold text-base text-[#1A1A1A]">{t("invoices.reviewModalTitle")}</div>
                <div className="text-xs text-slate-500">{review.po_folio} · {review.supplier_name}</div>
              </div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 space-y-1.5">
              {/* Fix WH68 (2026-10-09): lo que se compara es el subtotal sin IVA. */}
              {review.po_subtotal != null && (
                <>
                  <div className="flex justify-between text-sm"><span className="text-slate-600">{t("invoices.poSubtotal")}</span><span className="font-mono font-semibold">{fmtMXN(review.po_subtotal)}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-slate-600">{t("invoices.invoiceSubtotal")}</span><span className="font-mono font-semibold">{fmtMXN(review.subtotal)}</span></div>
                  <div className="flex justify-between text-sm pb-1.5 border-b border-red-200"><span className="font-bold text-red-800">{t("invoices.subtotalDifference")}</span><span className="font-mono font-bold text-red-700">{review.subtotal_difference > 0 ? "+" : ""}{fmtMXN(review.subtotal_difference)} ({review.subtotal_difference_percent}%)</span></div>
                </>
              )}
              <div className="flex justify-between text-sm"><span className="text-slate-600">{t("invoices.poTotal")}</span><span className="font-mono font-semibold">{fmtMXN(review.po_total)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-slate-600">{t("invoices.invoiceTotal")}</span><span className="font-mono font-semibold">{fmtMXN(review.total)}</span></div>
              <div className="flex justify-between text-sm pt-1.5 border-t border-red-200"><span className="font-bold text-red-800">{t("invoices.difference")}</span><span className="font-mono font-bold text-red-700">{review.total_difference > 0 ? "+" : ""}{fmtMXN(review.total_difference)} ({review.difference_percent}%)</span></div>
            </div>
            <textarea
              data-testid="invoice-review-reason"
              value={reviewReason}
              onChange={(e) => setReviewReason(e.target.value)}
              placeholder={t("invoices.reviewReasonPh")}
              rows={3}
              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-300 mb-3"
            />
            <div className="flex gap-2">
              <button data-testid="invoice-review-justify-btn" disabled={busy || reviewReason.trim().length < 5} onClick={() => submitReview("justify")}
                className="flex-1 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-sm font-bold disabled:opacity-50">
                {t("invoices.justifyAction")}
              </button>
              {puedeRechazar && (
                <button data-testid="invoice-review-reject-btn" disabled={busy || reviewReason.trim().length < 5} onClick={() => submitReview("reject")}
                  className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-xl text-sm font-bold disabled:opacity-50">
                  {t("invoices.rejectAction")}
                </button>
              )}
            </div>
            <button onClick={() => setReview(null)} className="w-full mt-2 py-2 text-sm text-slate-500 hover:text-[#1A1A1A]">{t("common.cancel")}</button>
          </motion.div>
        </div>
      )}

      {toDelete && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !busy && setToDelete(null)}>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            data-testid="invoice-delete-modal"
            className="bg-white rounded-2xl border border-slate-200 p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-xl bg-red-100 text-red-600 flex items-center justify-center">
                <Trash2 className="w-5 h-5" />
              </div>
              <div>
                <div className="font-display font-bold text-base text-[#1A1A1A]">{t("invoices.deleteModalTitle")}</div>
                <div className="text-xs text-slate-500 font-mono">{toDelete.folio_fiscal} · {fmtMXN(toDelete.total)}</div>
              </div>
            </div>
            <p className="text-sm text-slate-700 leading-relaxed mb-3">{t("invoices.deleteModalIntro")}</p>
            <label className="block text-xs font-bold text-slate-600 mb-1">{t("invoices.deleteReasonLabel")}</label>
            <textarea
              data-testid="invoice-delete-reason"
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder={t("invoices.deleteReasonPh")}
              rows={3}
              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-300 mb-3"
            />
            {isHighValue(toDelete) && (
              <div data-testid="invoice-delete-highvalue-block" className="bg-amber-50 border border-amber-300 rounded-xl p-3 mb-3 space-y-2">
                <div className="flex items-start gap-2 text-amber-900">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <p className="text-xs font-semibold leading-relaxed">
                    {t("invoices.deleteHighValueWarning", { folio: toDelete.folio_fiscal })}
                  </p>
                </div>
                <input
                  data-testid="invoice-delete-folio-confirm"
                  type="text"
                  value={folioConfirm}
                  onChange={(e) => setFolioConfirm(e.target.value)}
                  placeholder={t("invoices.deleteHighValueTypeFolio")}
                  className="w-full px-3 py-2 bg-white border border-amber-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
                {folioConfirm && !folioMatches && (
                  <p className="text-xs text-red-600 font-semibold">{t("invoices.deleteHighValueMismatch")}</p>
                )}
              </div>
            )}
            <div className="flex gap-2">
              <button onClick={() => setToDelete(null)} disabled={busy}
                className="flex-1 py-2.5 bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-xl text-sm font-bold disabled:opacity-50">
                {t("common.cancel")}
              </button>
              <button data-testid="invoice-delete-submit-btn" disabled={busy || !canSubmitDelete} onClick={submitDelete}
                className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-bold disabled:opacity-50 flex items-center justify-center gap-2">
                <Trash2 className="w-4 h-4" /> {t("invoices.deleteAction")}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {toRestore && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !busy && setToRestore(null)}>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            data-testid="invoice-restore-modal"
            className="bg-white rounded-2xl border border-slate-200 p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center">
                <RotateCcw className="w-5 h-5" />
              </div>
              <div>
                <div className="font-display font-bold text-base text-[#1A1A1A]">{t("invoices.restoreModalTitle")}</div>
                <div className="text-xs text-slate-500 font-mono">{toRestore.folio_fiscal}</div>
              </div>
            </div>
            <textarea
              data-testid="invoice-restore-reason"
              value={restoreReason}
              onChange={(e) => setRestoreReason(e.target.value)}
              placeholder={t("invoices.restoreReasonPh")}
              rows={3}
              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-300 mb-3"
            />
            <div className="flex gap-2">
              <button onClick={() => setToRestore(null)} disabled={busy}
                className="flex-1 py-2.5 bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-xl text-sm font-bold disabled:opacity-50">
                {t("common.cancel")}
              </button>
              <button data-testid="invoice-restore-submit-btn" disabled={busy || restoreReason.trim().length < 10} onClick={submitRestore}
                className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-bold disabled:opacity-50 flex items-center justify-center gap-2">
                <RotateCcw className="w-4 h-4" /> {t("invoices.restoreAction")}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
}
